import { useFlag } from '@automata/utils/hooks';
import { Argument } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import cloneDeep from 'lodash/cloneDeep';
import reduce from 'lodash/reduce';
import set from 'lodash/set';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { validateField } from '../../../utils';
import {
  DriverSchema,
  FieldValue,
  Instrument,
  WorkcellConfig,
  WorkcellErrors,
} from '../types';
import {
  compareFieldValues,
  compareFormValues,
  compareInstrumentValues,
  enrichConfig,
  removeExtraFields,
  validateInstrument,
} from '../utils';

export function useWorkcellForm(
  initialConfig: WorkcellConfig,
  driversByInstrumentId: Record<string, { configuration: DriverSchema }>,
  onClose: () => void,
  defaultWorkcellConfig: WorkcellConfig
) {
  const [formValues, setFormValues] = useState<WorkcellConfig>(
    cloneDeep(initialConfig)
  );
  const [preparedInstruments, setPreparedInstruments] = useState<
    [string, Instrument][]
  >([]);
  const [errors, setErrors] = useState<WorkcellErrors>({});

  const isSftpInstrumentEnabled = useFlag('fe.enable_sftp_instrument_editing');
  const isIgnoreErrorsSftpConfigEnabled = useFlag(
    'fe.enable_ignore_errors_sftp_config'
  );

  const allowedToEditInstruments = isSftpInstrumentEnabled ? ['sftp'] : [];

  useEffect(() => {
    const cleaned = removeExtraFields(
      formValues,
      defaultWorkcellConfig,
      initialConfig
    );
    const instruments: typeof preparedInstruments = [];
    const allErrors: WorkcellErrors = {};

    for (const [name, instrument] of Object.entries(cleaned)) {
      const isEmpty =
        !instrument.config || Object.keys(instrument.config).length === 0;
      if (
        isEmpty ||
        (instrument.driverName?.startsWith('automata_') &&
          !allowedToEditInstruments.includes(name))
      )
        continue;

      let schema = driversByInstrumentId[name]?.configuration;
      if (!schema) continue;

      if (name === 'sftp' && !isIgnoreErrorsSftpConfigEnabled) {
        const { ignore_errors, ...filteredSchema } = schema;
        schema = filteredSchema;
      }

      const enriched = enrichConfig(instrument.config, schema);
      const validationErrors = validateInstrument(enriched, schema);

      instruments.push([name, { ...instrument, config: enriched }]);
      allErrors[name] = validationErrors;
    }

    setPreparedInstruments(instruments);
    setErrors(allErrors);
  }, [driversByInstrumentId, defaultWorkcellConfig, formValues, initialConfig]);

  const canSave = useMemo(() => {
    const existingErrors = reduce(
      errors,
      (acc, instrumentErrors) => {
        if (!instrumentErrors) {
          return acc;
        }

        return acc + Object.values(instrumentErrors).filter(Boolean).length;
      },
      0
    );

    return existingErrors === 0;
  }, [errors]);

  const isFieldChanged = useCallback(
    (instrumentName: string, fieldKey: string): boolean => {
      const fieldSchema =
        driversByInstrumentId[instrumentName]?.configuration?.[fieldKey];
      const currentValue = formValues[instrumentName]?.config?.[fieldKey];
      const defaultValue =
        defaultWorkcellConfig[instrumentName]?.config?.[fieldKey];

      return compareFieldValues(currentValue, defaultValue, fieldSchema);
    },
    [formValues, defaultWorkcellConfig, driversByInstrumentId]
  );

  const isSimulateFieldChanged = useCallback(
    (instrumentName: string): boolean => {
      const currentValue = formValues[instrumentName]?.simulate;
      const defaultValue = defaultWorkcellConfig[instrumentName]?.simulate;

      return currentValue !== defaultValue;
    },
    [formValues, defaultWorkcellConfig]
  );

  const isInstrumentChanged = useCallback(
    (instrumentName: string): boolean => {
      const instrumentSchema =
        driversByInstrumentId[instrumentName]?.configuration;
      const currentInstrument = formValues[instrumentName]?.config;
      const defaultInstrument = defaultWorkcellConfig[instrumentName]?.config;

      return (
        compareInstrumentValues(
          currentInstrument,
          defaultInstrument,
          instrumentSchema
        ) || isSimulateFieldChanged(instrumentName)
      );
    },
    [
      driversByInstrumentId,
      formValues,
      defaultWorkcellConfig,
      isSimulateFieldChanged,
    ]
  );

  const isFormChanged = useCallback(
    (valuesToCompare: WorkcellConfig) => {
      return compareFormValues(
        formValues,
        valuesToCompare,
        driversByInstrumentId
      );
    },
    [driversByInstrumentId, formValues]
  );

  const resetField = useCallback(
    (instrumentName: string, fieldKey: string) => {
      const baselineValue =
        defaultWorkcellConfig[instrumentName]?.config?.[fieldKey];

      setFormValues((prev) => {
        const updated = cloneDeep(prev);
        if (!updated[instrumentName]) return updated;

        if (baselineValue === undefined) {
          delete updated[instrumentName].config[fieldKey];
        } else {
          updated[instrumentName].config[fieldKey] = baselineValue;
        }

        return updated;
      });

      setErrors((prevErrors) => {
        const updated = cloneDeep(prevErrors);
        delete updated[instrumentName]?.[fieldKey];
        return updated;
      });
    },
    [defaultWorkcellConfig]
  );

  const validateAndUpdate = useCallback(
    (instrumentName: string, key: string, value: FieldValue) => {
      const configMeta = driversByInstrumentId[instrumentName]?.configuration[
        key
      ] as Argument;

      if (configMeta) {
        const error = validateField(value, configMeta);
        setErrors((prevErrors) => {
          const updated = cloneDeep(prevErrors);
          return set(updated, [instrumentName, key], error);
        });
      }

      setFormValues((prev) => {
        const updated = cloneDeep(prev);
        return set(updated, [instrumentName, 'config', key], value);
      });
    },
    [driversByInstrumentId]
  );

  const updateSimulateField = useCallback(
    (instrumentName: string, value: boolean) => {
      setFormValues((prev) => {
        const updated = cloneDeep(prev);
        return set(updated, [instrumentName, 'simulate'], value);
      });
    },
    []
  );

  const handleSave = useCallback(() => {
    if (canSave) {
      onClose();
    }
  }, [canSave, onClose]);

  const resetForm = useCallback(() => {
    setFormValues(cloneDeep(initialConfig));
    setErrors({});
  }, [initialConfig]);

  const resetToDefaultWorkcell = useCallback(() => {
    setFormValues(cloneDeep(defaultWorkcellConfig));
    setErrors({});
  }, [defaultWorkcellConfig]);

  return {
    canSave,
    errors,
    formValues,
    preparedInstruments,
    isFieldChanged,
    isInstrumentChanged,
    isFormChanged,
    handleSave,
    resetField,
    resetForm,
    resetToDefaultWorkcell,
    validateAndUpdate,
    updateSimulateField,
    isSimulateFieldChanged,
  };
}
